import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { apiKey } from "../../config/config";
import { languageHelper } from "../../config/helperFunctions";
import { FaCog, FaSignOutAlt } from "react-icons/fa";

function PosSetupDashboard() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [formData, setFormData] = useState({
    pos_setup_date: "",
    first_invoice_date: "",
    renewal_date: "",
    payment_status: "Paid",
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("administration-token");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  };

  // Fetch global RMS POS Setup details
  const fetchPosSetup = () => {
    axios
      .get(`${apiKey}/api/administration/pos-setup`, { headers: getAuthHeaders() })
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          const data = res.data.data;
          setFormData({
            pos_setup_date: data.pos_setup_date || "",
            first_invoice_date: data.first_invoice_date || "",
            renewal_date: data.renewal_date || "",
            payment_status: data.payment_status || "Paid",
          });
        }
      })
      .catch((err) => {
        console.error(err);
        if (err?.response?.status === 401) {
          handleLogout();
        } else {
          toast.error(languageHelper("Failed to fetch administration settings"), {
            position: "bottom-right",
            autoClose: 4000,
            theme: "colored",
          });
        }
      })
      .finally(() => setPageLoading(false));
  };

  useEffect(() => {
    fetchPosSetup();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    axios
      .post(`${apiKey}/api/administration/pos-setup`, formData, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        if (res.data?.success) {
          toast.success(
            languageHelper(res.data.message || "Administration settings saved successfully"),
            {
              position: "bottom-right",
              autoClose: 4000,
              theme: "colored",
            }
          );
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          languageHelper(err?.response?.data?.message || "Error saving administration settings"),
          {
            position: "bottom-right",
            autoClose: 5000,
            theme: "colored",
          }
        );
      })
      .finally(() => setSubmitting(false));
  };

  const handleLogout = () => {
    const token = localStorage.getItem("administration-token");
    if (token) {
      axios
        .post(
          `${apiKey}/api/administration/logout`,
          {},
          { headers: getAuthHeaders() }
        )
        .catch((err) => console.log(err));
    }
    localStorage.removeItem("administration-token");
    localStorage.removeItem("administration-user");
    toast.info(languageHelper("Logged out successfully"), {
      position: "bottom-right",
      autoClose: 3000,
    });
    navigate("/administration/login");
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F0F2F6]">
        <img src="/assets/gif/dash2.gif" alt="Loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F6] text-slate-800 font-sans p-6 md:p-10">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <FaCog className="text-[#D97706] text-2xl md:text-3xl" />
              <h1 className="text-2xl md:text-3xl font-extrabold text-[#7029C4] tracking-tight">
                {languageHelper("Administration Settings")}
              </h1>
            </div>
            <p className="text-slate-500 text-xs md:text-sm mt-1 font-medium">
              {languageHelper("Please update administration dates and payment status below.")}
            </p>
          </div>

          {/* Logout Developer Button */}
          <button
            onClick={handleLogout}
            className="bg-[#FEE2E2] hover:bg-[#FCA5A5] text-[#DC2626] font-bold py-2.5 px-4 rounded-xl flex items-center gap-2 text-xs border border-red-200/80 shadow-xs transition-all cursor-pointer"
          >
            <FaSignOutAlt className="text-[#DC2626] text-xs" />
            <span>{languageHelper("Logout Developer")}</span>
          </button>
        </div>

        {/* Main Form Card Container */}
        <div className="bg-white rounded-[20px] shadow-sm border border-slate-200/80 p-8 md:p-10">
          {/* Section Tab Badge */}
          <div className="bg-[#F3E8FF] text-[#7029C4] font-bold text-xs py-2.5 px-5 rounded-xl inline-block mb-8 shadow-xs border border-purple-100">
            {languageHelper("Subscription & Administration Dates")}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
              {/* POS Setup Date */}
              <div>
                <label className="block text-[#7029C4] font-bold text-xs uppercase tracking-wider mb-2">
                  {languageHelper("POS Setup Date")}
                </label>
                <input
                  type="date"
                  name="pos_setup_date"
                  value={formData.pos_setup_date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:border-[#7029C4] focus:ring-2 focus:ring-[#7029C4]/20 text-slate-800 text-sm outline-none bg-white font-medium transition-all shadow-xs"
                />
              </div>

              {/* First Invoice Date */}
              <div>
                <label className="block text-[#7029C4] font-bold text-xs uppercase tracking-wider mb-2">
                  {languageHelper("First Invoice Date")}
                </label>
                <input
                  type="date"
                  name="first_invoice_date"
                  value={formData.first_invoice_date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:border-[#7029C4] focus:ring-2 focus:ring-[#7029C4]/20 text-slate-800 text-sm outline-none bg-white font-medium transition-all shadow-xs"
                />
              </div>

              {/* Renewal Date */}
              <div>
                <label className="block text-[#7029C4] font-bold text-xs uppercase tracking-wider mb-2">
                  {languageHelper("Renewal Date")}
                </label>
                <input
                  type="date"
                  name="renewal_date"
                  value={formData.renewal_date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:border-[#7029C4] focus:ring-2 focus:ring-[#7029C4]/20 text-slate-800 text-sm outline-none bg-white font-medium transition-all shadow-xs"
                />
              </div>

              {/* Payment Status */}
              <div>
                <label className="block text-[#7029C4] font-bold text-xs uppercase tracking-wider mb-2">
                  {languageHelper("Payment Status")}
                </label>
                <select
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:border-[#7029C4] focus:ring-2 focus:ring-[#7029C4]/20 text-slate-800 text-sm outline-none bg-white font-medium transition-all shadow-xs"
                >
                  <option value="Paid">{languageHelper("Paid")}</option>
                  <option value="Unpaid">{languageHelper("Unpaid")}</option>
                </select>
              </div>
            </div>

            {/* Save Settings Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#7029C4] hover:bg-[#5E21AC] text-white font-bold py-3.5 px-8 rounded-xl shadow-md shadow-[#7029C4]/20 text-sm transition-all cursor-pointer relative disabled:opacity-70 inline-flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <img className="w-5 h-5 mx-auto" src="/assets/gif/button_loader.gif" alt="Loading" />
                    <span>{languageHelper("Saving...")}</span>
                  </span>
                ) : (
                  <span>{languageHelper("Save Settings")}</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PosSetupDashboard;
